/**
 * Parking Lot Mobile Mammo / clinic-day ScheduleRestriction helpers (RPP-318).
 * Entry is GUID-only: /mobile/{guid}. Hours/overrides come from live flyer definition.
 * End times are inclusive (D3). Dates/times are practice wall clocks (D8).
 */

export interface ScheduleRestrictionHours {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

export interface ScheduleRestrictionDateOverride {
  overrideDate: string;
  closed: boolean;
  startTime?: string | null;
  endTime?: string | null;
}

export interface ScheduleRestriction {
  patientPortalMobileSchedulingKey?: number;
  guid?: string;
  /** Practice wall clock `yyyy-MM-ddTHH:mm:ss` — outer search bound (event start). */
  startRange: string;
  /** Practice wall clock inclusive end — outer search bound (event end of day). */
  endRange: string;
  eventStartDate?: string;
  eventEndDate?: string;
  siteGroup: string;
  showInsurance: boolean;
  showPrescription: boolean;
  locationName?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  hours?: ScheduleRestrictionHours[];
  dateOverrides?: ScheduleRestrictionDateOverride[];
}

export const SCHEDULE_RESTRICTION_STORAGE_KEY = 'pp_schedule_restriction';

const WALL_CLOCK_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/** Pad and build a practice wall-clock string (no timezone offset). */
export function wallClockFromParts(
  y: number,
  mo: number,
  d: number,
  h: number,
  mi: number,
  s = 0,
): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${y}-${p(mo)}-${p(d)}T${p(h)}:${p(mi)}:${p(s)}`;
}

export type ClinicDayLinkError =
  | 'invalid_dates'
  | 'end_before_start'
  | 'expired'
  | 'missing_site_group'
  | 'inactive'
  | 'not_found';

/** Inclusive EndRange: expired only when practiceLocalNow is strictly after end. */
export function validateClinicDayWindow(
  startRangeWall: string,
  endRangeWall: string,
  practiceLocalNow?: string | null,
): ClinicDayLinkError | null {
  if (!(endRangeWall > startRangeWall)) return 'end_before_start';
  const now = (practiceLocalNow ?? '').trim() || wallClockFromParts(
    new Date().getFullYear(),
    new Date().getMonth() + 1,
    new Date().getDate(),
    new Date().getHours(),
    new Date().getMinutes(),
    new Date().getSeconds(),
  );
  if (now > endRangeWall) return 'expired';
  return null;
}

export function clinicDayMaxSelectableDate(endRangeWall: string): string {
  const m = WALL_CLOCK_RE.exec(endRangeWall.trim());
  if (!m) {
    return endRangeWall.slice(0, 10);
  }
  return `${m[1]}-${m[2]}-${m[3]}`;
}

/** Build patient URL path for a saved flyer GUID. */
export function buildMobileClinicDayPath(guid: string): string {
  return `/mobile/${encodeURIComponent(guid)}`;
}

export function formatEventAddress(r: ScheduleRestriction): string | null {
  const name = r.locationName?.trim() || '';
  const addressBits = [
    r.addressLine1,
    r.addressLine2,
    [r.city, r.state].filter(Boolean).join(', '),
    r.zipcode,
  ].filter((p) => !!p && String(p).trim());
  if (name && addressBits.length) return `${name} · ${addressBits.join(' · ')}`;
  if (name) return name;
  return addressBits.length ? addressBits.join(' · ') : null;
}

/** Map IsAppointmentReschedulable / study PortalScheduleRestrictions snapshot → wizard restriction. */
export function restrictionFromReschedulability(dto: {
  startRange?: string | null;
  endRange?: string | null;
  siteGroup?: string | null;
  showInsurance?: boolean | null;
  showPrescription?: boolean | null;
  patientPortalMobileSchedulingKey?: number | null;
  guid?: string | null;
  locationName?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  hours?: Array<{ dayOfWeek: number; startTime: string; endTime: string }>;
  dateOverrides?: Array<{
    overrideDate: string;
    closed: boolean;
    startTime?: string | null;
    endTime?: string | null;
  }>;
}): ScheduleRestriction | undefined {
  const startRange = dto.startRange ? (normalizeToWallClock(dto.startRange) ?? dto.startRange) : '';
  const endRange = dto.endRange ? (normalizeToWallClock(dto.endRange) ?? dto.endRange) : '';
  const siteGroup = dto.siteGroup?.trim() ?? '';
  if (!startRange && !endRange && !siteGroup && !dto.locationName?.trim()) return undefined;

  return {
    patientPortalMobileSchedulingKey: dto.patientPortalMobileSchedulingKey ?? undefined,
    guid: dto.guid ?? undefined,
    startRange,
    endRange,
    siteGroup,
    showInsurance: dto.showInsurance ?? true,
    showPrescription: dto.showPrescription ?? true,
    locationName: dto.locationName,
    addressLine1: dto.addressLine1,
    addressLine2: dto.addressLine2,
    city: dto.city,
    state: dto.state,
    zipcode: dto.zipcode,
    hours: (dto.hours ?? []).map((h) => ({
      dayOfWeek: h.dayOfWeek,
      startTime: normalizeTime(h.startTime),
      endTime: normalizeTime(h.endTime),
    })),
    dateOverrides: (dto.dateOverrides ?? []).map((o) => ({
      overrideDate: String(o.overrideDate).slice(0, 10),
      closed: o.closed,
      startTime: o.startTime ? normalizeTime(o.startTime) : null,
      endTime: o.endTime ? normalizeTime(o.endTime) : null,
    })),
  };
}

/** Map public API flyer DTO → ScheduleRestriction used by the wizard. */
export function restrictionFromMobileSchedulingPublic(dto: {
  patientPortalMobileSchedulingKey: number;
  guid: string;
  siteGroupCode: string;
  description?: string | null;
  locationName?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  eventStartDate: string;
  eventEndDate: string;
  showInsuranceStep: boolean;
  showPrescriptionStep: boolean;
  hours?: Array<{ dayOfWeek: number; startTime: string; endTime: string; active?: boolean }>;
  dateOverrides?: Array<{
    overrideDate: string;
    closed: boolean;
    startTime?: string | null;
    endTime?: string | null;
    active?: boolean;
  }>;
}): ScheduleRestriction {
  const startDate = String(dto.eventStartDate).slice(0, 10);
  const endDate = String(dto.eventEndDate).slice(0, 10);
  return {
    patientPortalMobileSchedulingKey: dto.patientPortalMobileSchedulingKey,
    guid: dto.guid,
    startRange: `${startDate}T00:00:00`,
    endRange: `${endDate}T23:59:59`,
    eventStartDate: startDate,
    eventEndDate: endDate,
    siteGroup: dto.siteGroupCode,
    showInsurance: dto.showInsuranceStep,
    showPrescription: dto.showPrescriptionStep,
    locationName: dto.locationName,
    addressLine1: dto.addressLine1,
    addressLine2: dto.addressLine2,
    city: dto.city,
    state: dto.state,
    zipcode: dto.zipcode,
    hours: (dto.hours ?? [])
      .filter((h) => h.active !== false)
      .map((h) => ({
        dayOfWeek: h.dayOfWeek,
        startTime: normalizeTime(h.startTime),
        endTime: normalizeTime(h.endTime),
      })),
    dateOverrides: (dto.dateOverrides ?? [])
      .filter((o) => o.active !== false)
      .map((o) => ({
        overrideDate: String(o.overrideDate).slice(0, 10),
        closed: o.closed,
        startTime: o.startTime ? normalizeTime(o.startTime) : null,
        endTime: o.endTime ? normalizeTime(o.endTime) : null,
      })),
  };
}

function normalizeTime(value: string): string {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(value.trim());
  if (!m) return value;
  const p = (n: string) => n.padStart(2, '0');
  return `${p(m[1])}:${p(m[2])}:${p(m[3] ?? '00')}`;
}

function timeToMinutes(t: string): number | null {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(t.trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** Whether a wall-clock slot datetime is allowed by hours/overrides (inclusive end).
 * Open windows = open overrides for the date if any, else DOW hours.
 * Closed overrides always win (all-day when times omitted; otherwise that window only).
 */
export function isSlotAllowedByMobileHours(
  restriction: ScheduleRestriction,
  slotWallDateTime: string,
): boolean {
  const m = WALL_CLOCK_RE.exec(slotWallDateTime.trim());
  if (!m) return true;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dateStr = `${m[1]}-${m[2]}-${m[3]}`;
  const mins = Number(m[4]) * 60 + Number(m[5]);
  const dow = new Date(y, mo - 1, d).getDay();

  const overrides = (restriction.dateOverrides ?? []).filter((o) => o.overrideDate === dateStr);

  for (const closed of overrides.filter((o) => o.closed)) {
    const hasStart = !!closed.startTime?.trim();
    const hasEnd = !!closed.endTime?.trim();
    if (!hasStart && !hasEnd) return false; // all-day close wins
    if (hasStart && hasEnd) {
      const start = timeToMinutes(closed.startTime!);
      const end = timeToMinutes(closed.endTime!);
      if (start != null && end != null && mins >= start && mins <= end) return false;
    }
  }

  const openOverrides = overrides.filter((o) => !o.closed);
  if (openOverrides.length > 0) {
    return openOverrides.some((w) => {
      const start = w.startTime ? timeToMinutes(w.startTime) : null;
      const end = w.endTime ? timeToMinutes(w.endTime) : null;
      if (start == null || end == null) return false;
      return mins >= start && mins <= end;
    });
  }

  const windows = (restriction.hours ?? []).filter((h) => h.dayOfWeek === dow);
  if (windows.length === 0) return !(restriction.hours && restriction.hours.length > 0);
  return windows.some((w) => {
    const start = timeToMinutes(w.startTime);
    const end = timeToMinutes(w.endTime);
    if (start == null || end == null) return false;
    return mins >= start && mins <= end;
  });
}

export function persistScheduleRestriction(r: ScheduleRestriction | null): void {
  try {
    if (!r) {
      sessionStorage.removeItem(SCHEDULE_RESTRICTION_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(SCHEDULE_RESTRICTION_STORAGE_KEY, JSON.stringify(r));
  } catch {
    /* ignore quota / private mode */
  }
}

export function loadPersistedScheduleRestriction(): ScheduleRestriction | null {
  try {
    const raw = sessionStorage.getItem(SCHEDULE_RESTRICTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ScheduleRestriction;
    if (!parsed?.siteGroup || !parsed.startRange || !parsed.endRange) return null;
    return {
      ...parsed,
      startRange: normalizeToWallClock(parsed.startRange) ?? parsed.startRange,
      endRange: normalizeToWallClock(parsed.endRange) ?? parsed.endRange,
      showInsurance: !!parsed.showInsurance,
      showPrescription: !!parsed.showPrescription,
    };
  } catch {
    return null;
  }
}

function normalizeToWallClock(value: string): string | null {
  if (WALL_CLOCK_RE.test(value.trim())) {
    const m = WALL_CLOCK_RE.exec(value.trim())!;
    return wallClockFromParts(
      Number(m[1]),
      Number(m[2]),
      Number(m[3]),
      Number(m[4]),
      Number(m[5]),
      Number(m[6] ?? '0'),
    );
  }
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return wallClockFromParts(
    dt.getFullYear(),
    dt.getMonth() + 1,
    dt.getDate(),
    dt.getHours(),
    dt.getMinutes(),
    dt.getSeconds(),
  );
}

/**
 * Loads sessionStorage restriction only when the clinic-day window is still valid.
 * Clears stale / expired / tampered entries so normal GuestSchedule is not constrained.
 */
export function loadValidPersistedScheduleRestriction(
  practiceLocalNow?: string | null,
): ScheduleRestriction | null {
  const r = loadPersistedScheduleRestriction();
  if (!r) return null;
  const err = validateClinicDayWindow(r.startRange, r.endRange, practiceLocalNow);
  if (err) {
    persistScheduleRestriction(null);
    return null;
  }
  return r;
}

/** @deprecated Legacy URL segments — no longer used for entry. */
export function parseClinicDayWallClock(segment: string): string | null {
  if (!/^\d{12}$/.test(segment)) return null;
  const y = Number(segment.slice(0, 4));
  const mo = Number(segment.slice(4, 6));
  const d = Number(segment.slice(6, 8));
  const h = Number(segment.slice(8, 10));
  const mi = Number(segment.slice(10, 12));
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return null;
  return wallClockFromParts(y, mo, d, h, mi, 0);
}

export function parseInsuranceStepQuery(raw: string | null | undefined): boolean {
  if (raw == null || raw === '') return true;
  const v = raw.trim().toLowerCase();
  return !(v === 'false' || v === 'f' || v === 'n' || v === '0' || v === 'no');
}

export function parsePrescriptionStepQuery(raw: string | null | undefined): boolean {
  if (raw == null || raw === '') return false;
  const v = raw.trim().toLowerCase();
  return v === 'true' || v === 't' || v === 'y' || v === '1' || v === 'yes';
}

export function parseBoolQuery(raw: string | null | undefined, defaultValue: boolean): boolean {
  if (raw == null || raw === '') return defaultValue;
  const v = raw.trim().toLowerCase();
  if (v === 'true' || v === 't' || v === 'y' || v === '1' || v === 'yes') return true;
  if (v === 'false' || v === 'f' || v === 'n' || v === '0' || v === 'no') return false;
  return defaultValue;
}
